<template>
  <div class="pdf-viewer" :class="{ 'is-loading': loading, 'is-fullscreen': isFullscreen }">

    <div v-if="loading" class="pdf-loading">
      <b-spinner variant="primary" label="Loading PDF"></b-spinner>
      <span class="ml-2">{{ $t('loading') || 'Loading document...' }}</span>
    </div>

    <div v-if="error" class="pdf-error">
      <b-alert show variant="danger">
        <h4 class="alert-heading">{{ $t('pdf.error') || 'Error loading PDF' }}</h4>
        <p>{{ error }}</p>
        <div class="d-flex justify-content-between align-items-center mt-3">
          <b-button variant="outline-danger" @click="reloadViewer">
            <font-awesome-icon icon="sync-alt" class="mr-1" />
            {{ $t('retry') || 'Retry' }}
          </b-button>
          <b-button
            variant="primary"
            :href="src"
            target="_blank"
          >
            <font-awesome-icon icon="external-link-alt" class="mr-1" />
            {{ $t('pdf.openInNewTab') || 'Open in New Tab' }}
          </b-button>
        </div>
      </b-alert>
    </div>


    <div v-if="showControls && !loading && !error" class="pdf-controls d-flex justify-content-center align-items-center flex-wrap">
 
      <b-button-group size="sm" class="mx-1">
        <b-button
          :disabled="currentPage <= 1"
          @click="previousPage"
          variant="outline-primary"
          v-b-tooltip.hover
          :title="$t('pdf.previousPage') || 'Previous Page'"
        >
          <font-awesome-icon icon="chevron-left" fixed-width />
        </b-button>
        <b-button
          variant="outline-primary"
          class="px-3 page-display"
        >
          {{ currentPage }} / {{ numPages }}
        </b-button>
        <b-button
          :disabled="currentPage >= numPages"
          @click="nextPage"
          variant="outline-primary"
          v-b-tooltip.hover
          :title="$t('pdf.nextPage') || 'Next Page'"
        >
          <font-awesome-icon icon="chevron-right" fixed-width />
        </b-button>
      </b-button-group>


      <b-button-group size="sm" class="mx-1">
        <b-button
          @click="zoomOut"
          variant="outline-primary"
          v-b-tooltip.hover
          :title="$t('pdf.zoomOut') || 'Zoom Out'"
        >
          <font-awesome-icon icon="search-minus" fixed-width />
        </b-button>
        <b-dropdown
          variant="outline-primary"
          size="sm"
          :text="Math.round(scale * 100) + '%'"
          class="zoom-dropdown"
        >
          <b-dropdown-item @click="setZoom(0.5)">50%</b-dropdown-item>
          <b-dropdown-item @click="setZoom(0.75)">75%</b-dropdown-item>
          <b-dropdown-item @click="setZoom(1)">100%</b-dropdown-item>
          <b-dropdown-item @click="setZoom(1.25)">125%</b-dropdown-item>
          <b-dropdown-item @click="setZoom(1.5)">150%</b-dropdown-item>
          <b-dropdown-item @click="setZoom(2)">200%</b-dropdown-item>
        </b-dropdown>
        <b-button
          @click="zoomIn"
          variant="outline-primary"
          v-b-tooltip.hover
          :title="$t('pdf.zoomIn') || 'Zoom In'"
        >
          <font-awesome-icon icon="search-plus" fixed-width />
        </b-button>
      </b-button-group>

      <b-button-group size="sm" class="mx-1">
        <b-button
          @click="rotate"
          variant="outline-primary"
          v-b-tooltip.hover
          :title="$t('pdf.rotate') || 'Rotate'"
        >
          <font-awesome-icon icon="redo" fixed-width />
        </b-button>
        <b-button
          v-if="allowFullscreen"
          @click="toggleFullscreen"
          variant="outline-primary"
          v-b-tooltip.hover
          :title="isFullscreen ? $t('pdf.exitFullscreen') || 'Exit Fullscreen' : $t('pdf.enterFullscreen') || 'Enter Fullscreen'"
        >
          <font-awesome-icon :icon="isFullscreen ? 'compress' : 'expand'" fixed-width />
        </b-button>
      </b-button-group>
    </div>

    <div class="pdf-canvas-container" ref="canvasContainer">
      <canvas ref="pdfCanvas"></canvas>
    </div>
  </div>
</template>

<script>
// Import PDF.js with proper CommonJS imports for Vue 2
const pdfjsLib = require('pdfjs-dist/legacy/build/pdf');
const pdfjsWorker = require('pdfjs-dist/legacy/build/pdf.worker.entry');

import {
  BSpinner,
  BAlert,
  BButton,
  BButtonGroup,
  BFormInput,
  BDropdown,
  BDropdownItem,
  VBTooltip
} from 'bootstrap-vue';


pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

export default {
  name: 'PdfViewer',

  components: {
    BSpinner,
    BAlert,
    BButton,
    BButtonGroup,
    BFormInput,
    BDropdown,
    BDropdownItem
  },

  directives: {
    'b-tooltip': VBTooltip
  },

  props: {
    src: {
      type: String,
      required: true
    },
    showControls: {
      type: Boolean,
      default: true
    },
    allowFullscreen: {
      type: Boolean,
      default: true
    },
    initialPage: {
      type: Number,
      default: 1
    },
    initialScale: {
      type: Number,
      default: 1
    }
  },

  data() {
    return {
      loading: true,
      error: null,
      currentPage: this.initialPage,
      numPages: 0,
      scale: this.initialScale,
      rotation: 0,
      isFullscreen: false,
      pdfDoc: null,
      pageRendering: false,
      pageNumPending: null
    };
  },

  methods: {
    async loadPDF() {
      try {
        this.loading = true;
        this.error = null;
        console.log('Loading PDF from URL:', this.src);

        // Load the PDF document with updated configuration
        const loadingTask = pdfjsLib.getDocument({
          url: this.src,
          verbosity: 0,
          useSystemFonts: true,
          cMapUrl: `${window.location.origin}/pdf.js/cmaps/`,
          cMapPacked: true,
          enableXfa: false,
          useWorkerFetch: false
        });

        this.pdfDoc = await loadingTask.promise;
        this.numPages = this.pdfDoc.numPages;
        
        // Emit loaded event
        this.$emit('loaded', {
          pagesCount: this.numPages,
          currentPage: this.currentPage
        });

        // Render the first page
        await this.renderPage(this.currentPage);
        
        this.loading = false;
      } catch (err) {
        console.error('Error loading PDF:', err);
        this.error = err.message || this.$t('pdf.loadError') || 'Failed to load PDF';
        this.loading = false;
        // Emit error event
        this.$emit('error', err);
      }
    },

    async renderPage(num) {
      if (this.pageRendering) {
        this.pageNumPending = num;
        return;
      }

      try {
        this.pageRendering = true;
        const page = await this.pdfDoc.getPage(num);

        const canvas = this.$refs.pdfCanvas;
        const ctx = canvas.getContext('2d');

        // Calculate viewport with current scale
        const viewport = page.getViewport({ 
          scale: this.scale,
          rotation: this.rotation 
        });

        canvas.width = viewport.width;
        canvas.height = viewport.height;


        await page.render({
          canvasContext: ctx,
          viewport: viewport
        }).promise;

        this.pageRendering = false;

        if (this.pageNumPending !== null) {
          await this.renderPage(this.pageNumPending);
          this.pageNumPending = null;
        }
      } catch (err) {
        console.error('Error rendering page:', err);
        this.error = err.message || this.$t('pdf.renderError') || 'Failed to render page';
        this.pageRendering = false;
      }
    },

    async previousPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
        await this.renderPage(this.currentPage);
        this.$emit('page-change', this.currentPage);
      }
    },

    async nextPage() {
      if (this.currentPage < this.numPages) {
        this.currentPage++;
        await this.renderPage(this.currentPage);
        this.$emit('page-change', this.currentPage);
      }
    },

    async setZoom(scale) {
      this.scale = scale;
      await this.renderPage(this.currentPage);
      this.$emit('scale-change', scale);
    },

    async zoomIn() {
      this.scale = Math.min(this.scale * 1.2, 3);
      await this.renderPage(this.currentPage);
      this.$emit('scale-change', this.scale);
    },

    async zoomOut() {
      this.scale = Math.max(this.scale / 1.2, 0.5);
      await this.renderPage(this.currentPage);
      this.$emit('scale-change', this.scale);
    },

    async rotate() {
      this.rotation = (this.rotation + 90) % 360;
      await this.renderPage(this.currentPage);
    },

    async reloadViewer() {
      await this.loadPDF();
    },

    async toggleFullscreen() {
      try {
        if (!this.isFullscreen) {
          await this.$el.requestFullscreen();
        } else {
          await document.exitFullscreen();
        }
      } catch (err) {
        console.error('Fullscreen error:', err);
      }
    }
  },

  watch: {
    src: {
      handler: 'loadPDF',
      immediate: true
    }
  },

  beforeDestroy() {
    if (this.pdfDoc) {
      this.pdfDoc.destroy();
      this.pdfDoc = null;
    }
  }
};
</script>

<style lang="scss" scoped>
.pdf-viewer {
  position: relative;
  width: 100%;
  height: 100%;
  max-height: 100%;
  min-height: 400px;
  background-color: var(--light, #f8f9fa);
  border: 1px solid var(--border-color, #dee2e6);
  border-radius: 0.25rem;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  &.is-fullscreen {
    border: none;
    border-radius: 0;
  }

  &.is-loading {
    display: flex;
    justify-content: center;
    align-items: center;
  }
}

.pdf-loading {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.9);
  padding: 1rem;
  border-radius: 0.25rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  z-index: 2;
}

.pdf-error {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 80%;
  max-width: 500px;
  z-index: 2;
}

.pdf-controls {
  position: sticky;
  top: 0;
  padding: 0.75rem;
  background: rgba(255, 255, 255, 0.95);
  border-bottom: 1px solid var(--border-color, #dee2e6);
  z-index: 1;
  backdrop-filter: blur(5px);
  gap: 0.5rem;

  .page-display {
    min-width: 80px;
    text-align: center;
    font-weight: 500;
    cursor: default;
    pointer-events: none;
  }

  .zoom-dropdown {
    ::v-deep .btn {
      min-width: 70px;
    }
  }

  svg {
    width: 14px;
    height: 14px;
  }

  // Responsive
  @media (max-width: 768px) {
    padding: 0.5rem;
    gap: 0.25rem;

    .mx-1 {
      margin: 0.25rem !important;
    }

    .page-display {
      min-width: 70px;
      font-size: 0.8rem;
    }

    .zoom-dropdown ::v-deep .btn {
      min-width: 60px;
    }
  }
}

.pdf-canvas-container {
  position: relative;
  width: 100%;
  flex: 1;
  min-height: 0;
  overflow: auto;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: var(--light, #f8f9fa);
  padding: 0;

  canvas {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    background-color: white;
    display: block;
    margin: auto;
  }
}

// Fullscreen styles
:fullscreen {
  background: var(--light, #f8f9fa);
  
  .pdf-canvas-container {
    height: calc(100vh - 56px);
  }
}
</style> 